import { faTimes } from '@fortawesome/free-solid-svg-icons'
import React, { useEffect, useState, useRef } from 'react'
import Popup from 'reactjs-popup'
import { Flex, Image, ImageProps, Box } from 'rebass'
import _ from 'lodash'

import ButtonFa from './ButtonFa'
import { buildMediaUrl } from '../services/cloudinary'
import IconButton from 'particles/Button/IconButton'
import Text from 'particles/Text'
import styled from 'particles/themes/styled'
import { useIsMobileView } from 'hooks'
import Icon from 'particles/Icon'

interface CarouselFooterProps {
    currentIndex: number
    maxIndex: number
    onClickLeft?: () => void
    onClickRight?: () => void
    footerColor?: 'primary' | 'primaryInverse'
}

export const CarouselFooter: React.FC<CarouselFooterProps> = ({
    currentIndex,
    maxIndex,
    onClickLeft,
    onClickRight,
    footerColor = 'primary',
}) => {
    return (
        <Flex
            mt={'12px'}
            sx={{
                gap: '24px',
            }}
            justifyContent="center"
            alignItems="center"
        >
            {onClickLeft && (
                <IconButton
                    form="icon"
                    size="s"
                    icon="arrowLeft"
                    onClick={e => {
                        e.stopPropagation()
                        onClickLeft()
                    }}
                    isDisabled={currentIndex <= 0}
                    color="neutral"
                    iconColor={currentIndex <= 0 ? undefined : footerColor}
                />
            )}
            <Text scale="bodyM500" color={footerColor}>{`${currentIndex + 1} / ${maxIndex + 1}`}</Text>
            {onClickRight && (
                <IconButton
                    icon="arrowRight"
                    size="s"
                    indicatorColor="darkGreen"
                    color="neutral"
                    form="icon"
                    onClick={e => {
                        e.stopPropagation()
                        onClickRight()
                    }}
                    isDisabled={currentIndex >= maxIndex}
                    iconColor={currentIndex >= maxIndex ? undefined : footerColor}
                />
            )}
        </Flex>
    )
}

export interface MediaData {
    image: string
    img_main: string
    resource_type: string
    public_id: string
    uuid: string
    file_format?: string
    source: 'cloudinary' | 'aws'
}

interface MediaDataWithUrl extends MediaData {
    url?: string
}

interface MediaViewerProps extends ImageProps {
    key?: string
    mediaData: MediaData | MediaData[]
    showMediaIndex?: number | undefined
    maxHeight?: string | undefined
    maxWidth?: string | undefined
    showPopup?: boolean
    showArrowButtons?: boolean
    popupOnly?: boolean
    isOpenModal?: boolean
    setModalOpen?: any
    setMediaData?: any
    setSelectedMediaIndex?: any
    style?: { [key: string]: string }
    showCarouselFooter?: boolean
    footerColor?: 'primary' | 'primaryInverse'
    playVideo?: boolean
    showCarouselFooterEmptyBlock?: boolean
    onMediaIdxChange?: (idx: number) => void
}

interface PopupContentProps {
    contentData: MediaDataWithUrl[]
    selectedIndex: number
    close: () => void
}

const PopupContent: React.FC<PopupContentProps> = ({ contentData, selectedIndex, close, ...mediaProps }) => {
    return (
        <>
            <Box style={{ position: 'absolute', right: 0, top: 0, zIndex: 1 }}>
                <ButtonFa icon={faTimes} iconSize="1x" onClick={close} />
            </Box>
            <Box sx={{ textAlign: 'center' }} maxHeight="100vh">
                {contentData[selectedIndex].resource_type === 'image' && (
                    <Image
                        src={contentData[selectedIndex].url}
                        style={{ maxHeight: '75vh', maxWidth: '95%', objectFit: 'contain' }}
                        {...mediaProps}
                    />
                )}
                {contentData[selectedIndex].resource_type === 'video' && (
                    <video
                        key={selectedIndex}
                        controls
                        style={{ maxHeight: '75vh', maxWidth: '95%', objectFit: 'contain' }}
                    >
                        <source src={contentData[selectedIndex].url} />
                    </video>
                )}
            </Box>
        </>
    )
}

const MediaViewer: React.FC<MediaViewerProps> = ({
    mediaData,
    showMediaIndex = undefined,
    maxHeight = undefined,
    maxWidth = undefined,
    showPopup = true,
    showArrowButtons = true,
    popupOnly = false, // if want to display popup in parent component and use redux to control it, please set it to true
    isOpenModal = false, // only be used if popupOnly = true
    setModalOpen = undefined, // only be used for control the popupOnly MediaViwer
    setMediaData = undefined, // only be used for control the popupOnly MediaViwer
    setSelectedMediaIndex = undefined, // only be used for control the popupOnly MediaViwer
    style = undefined,
    showCarouselFooter = true, // set to false if used in app preview
    footerColor = 'primary',
    onMediaIdxChange,
    playVideo = true,
    showCarouselFooterEmptyBlock = false,
    ...mediaProps
}) => {
    const [hasVideoStarted, setHasVideoStarted] = useState(false)
    let contentData: MediaDataWithUrl[]
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const isMobileView = useIsMobileView()
    if (_.isArray(mediaData)) {
        contentData = mediaData
    } else {
        contentData = [mediaData]
    }
    const totalContent = contentData.length
    const needsCarousel = totalContent > 1 && showCarouselFooter
    const maxIndex = totalContent - 1

    const [selectedIndex, setSelectedIndex] = useState<number>(showMediaIndex || 0)

    useEffect(() => {
        if (!contentData[selectedIndex] && setSelectedMediaIndex) {
            setSelectedMediaIndex(0)
        }
    }, [contentData.length])

    const goNext = () => {
        if (selectedIndex >= maxIndex) return
        onMediaIdxChange?.(selectedIndex + 1)
        setSelectedIndex(selectedIndex >= maxIndex ? 0 : selectedIndex + 1)
    }
    const goBack = () => {
        if (selectedIndex <= 0) return
        onMediaIdxChange?.(selectedIndex - 1)
        setSelectedIndex(selectedIndex === 0 ? maxIndex : selectedIndex - 1)
    }
    const mediaSelection = (data: MediaDataWithUrl) => {
        if (data?.resource_type === 'video') {
            return (
                <VideoContainer>
                    <video
                        ref={videoRef}
                        key={data.public_id}
                        src={data.url}
                        controls={isMobileView ? playVideo && hasVideoStarted : true}
                        height={'100%'}
                        style={{
                            maxWidth: maxWidth || '100%',
                            objectFit: 'contain',
                            maxHeight: maxHeight || '100%',
                            ...style,
                        }}
                        muted={false}
                        playsInline={true}
                        onPlay={() => {
                            if (isMobileView) {
                                setHasVideoStarted(true)
                            }
                        }}
                        onClick={(e: React.MouseEvent<HTMLVideoElement, MouseEvent>) => {
                            if (!playVideo) {
                                e.preventDefault()
                                return
                            }
                            // If video hasn't started yet and we're on mobile, handle play action
                            if (isMobileView && !hasVideoStarted) {
                                e.preventDefault()
                                e.stopPropagation()
                                if (videoRef.current) {
                                    videoRef.current.controls = true
                                    videoRef.current.play().catch(err => console.error('Error playing video:', err))
                                }
                                return
                            }
                        }}
                    />
                    {isMobileView && !hasVideoStarted && (
                        <PlayIconOverlay
                            onClick={e => {
                                e.preventDefault()
                                if (playVideo) {
                                    e.stopPropagation()
                                    if (videoRef.current) {
                                        videoRef.current.controls = true
                                        videoRef.current.play()
                                    }
                                    setHasVideoStarted(true)
                                }
                            }}
                        >
                            <div className="circle">
                                <Icon
                                    name="play"
                                    form="fill"
                                    size="24px"
                                    fillColor="primaryInverse"
                                    color="primaryInverse"
                                />
                            </div>
                        </PlayIconOverlay>
                    )}
                </VideoContainer>
            )
        }
        return (
            <Image
                src={data.url}
                height={'100%'}
                maxHeight={maxHeight}
                sx={{
                    ...style,
                    maxWidth: maxWidth || '100%',
                    objectFit: 'contain',
                }}
                onClick={() => {
                    if (setMediaData && setModalOpen && setSelectedMediaIndex) {
                        setMediaData(contentData)
                        setSelectedMediaIndex(selectedIndex)
                        setModalOpen(true)
                    }
                }}
            />
        )
    }

    useEffect(() => {
        if (showMediaIndex !== undefined) {
            setSelectedIndex(showMediaIndex)
        }
    }, [showMediaIndex])

    contentData = contentData.map(data => ({
        ...data,
        url: buildMediaUrl(data),
    }))

    if (!contentData || contentData.length === 0 || !contentData[selectedIndex]) return null

    if (popupOnly) {
        return (
            <>
                <Box minHeight={mediaProps.minHeight} maxHeight={maxHeight}>
                    <Popup
                        modal
                        open={isOpenModal}
                        closeOnDocumentClick
                        onOpen={() => setModalOpen(true)}
                        onClose={() => setModalOpen(false)}
                        contentStyle={{ maxWidth: '90%', width: 'auto', padding: '40px 40px 20px 40px' }}
                        overlayStyle={{ zIndex: 1001 }} // set z-index to 1001 is to avoid z fighting with edit request history modal
                    >
                        {close => (
                            <>
                                <PopupContent
                                    contentData={contentData}
                                    selectedIndex={selectedIndex}
                                    close={close}
                                    {...mediaProps}
                                />
                                {needsCarousel ? (
                                    <CarouselFooter
                                        footerColor={footerColor}
                                        currentIndex={selectedIndex}
                                        maxIndex={maxIndex}
                                        onClickLeft={showArrowButtons ? goBack : undefined}
                                        onClickRight={showArrowButtons ? goNext : undefined}
                                    />
                                ) : showCarouselFooterEmptyBlock ? (
                                    <EmptyBlock height={'40px'} />
                                ) : null}
                            </>
                        )}
                    </Popup>
                </Box>
            </>
        )
    } else {
        if (!showPopup) {
            return (
                <Flex flexDirection="column">
                    <Box
                        height={mediaProps.height || '75vh'}
                        minHeight={mediaProps.minHeight}
                        maxHeight={maxHeight}
                        sx={{
                            textAlign: 'center',
                        }}
                    >
                        {mediaSelection(contentData[selectedIndex])}
                    </Box>
                    {needsCarousel ? (
                        <CarouselFooter
                            currentIndex={selectedIndex}
                            maxIndex={maxIndex}
                            footerColor={footerColor}
                            onClickLeft={showArrowButtons ? goBack : undefined}
                            onClickRight={showArrowButtons ? goNext : undefined}
                        />
                    ) : showCarouselFooterEmptyBlock ? (
                        <EmptyBlock height={'40px'} />
                    ) : null}
                </Flex>
            )
        }

        // no need popup on video
        if (contentData[selectedIndex]?.resource_type === 'video') {
            return (
                <Box
                    height={mediaProps.height || 'fit-content'}
                    minHeight={mediaProps.minHeight}
                    maxHeight={maxHeight}
                    sx={{
                        alignContent: 'center',
                        textAlign: 'center',
                    }}
                >
                    {mediaSelection(contentData[selectedIndex])}
                    {needsCarousel ? (
                        <CarouselFooter
                            currentIndex={selectedIndex}
                            maxIndex={maxIndex}
                            onClickLeft={showArrowButtons ? goBack : undefined}
                            onClickRight={showArrowButtons ? goNext : undefined}
                        />
                    ) : showCarouselFooterEmptyBlock ? (
                        <EmptyBlock height={'40px'} />
                    ) : null}
                </Box>
            )
        }
        return (
            <>
                <Box
                    height={mediaProps.height || 'fit-content'}
                    minHeight={mediaProps.minHeight}
                    maxHeight={maxHeight}
                    sx={{
                        alignContent: 'center',
                        textAlign: 'center',
                    }}
                >
                    <Popup
                        modal
                        trigger={mediaSelection(contentData[selectedIndex])}
                        contentStyle={{ maxWidth: '90%', width: 'auto', padding: '40px 40px 20px 40px' }}
                    >
                        {close => (
                            <>
                                <PopupContent
                                    contentData={contentData}
                                    selectedIndex={selectedIndex}
                                    close={close}
                                    {...mediaProps}
                                />
                                {needsCarousel ? (
                                    <CarouselFooter
                                        currentIndex={selectedIndex}
                                        maxIndex={maxIndex}
                                        onClickLeft={showArrowButtons ? goBack : undefined}
                                        onClickRight={showArrowButtons ? goNext : undefined}
                                    />
                                ) : showCarouselFooterEmptyBlock ? (
                                    <EmptyBlock height={'40px'} />
                                ) : null}
                            </>
                        )}
                    </Popup>
                </Box>
                {needsCarousel ? (
                    <CarouselFooter
                        currentIndex={selectedIndex}
                        maxIndex={maxIndex}
                        footerColor={footerColor}
                        onClickLeft={showArrowButtons ? goBack : undefined}
                        onClickRight={showArrowButtons ? goNext : undefined}
                    />
                ) : showCarouselFooterEmptyBlock ? (
                    <EmptyBlock height={'40px'} />
                ) : null}
            </>
        )
    }
}

export default MediaViewer

const EmptyBlock = styled.div<{ height: string }>`
    height: ${({ height }) => height};
`

const VideoContainer = styled.div`
    position: relative;
    display: inline-block;
    width: 100%;
    height: 100%;

    // to always show default play button
    video::-webkit-media-controls-play-button {
        display: block !important;
    }
`

const PlayIconOverlay = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    cursor: pointer;
    z-index: 1;
    background-color: rgba(0, 0, 0, 0.6);
    border-radius: 50%;
    width: 64px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;

    .circle {
        box-sizing: border-box;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        border: 4px solid ${({ theme }) => theme.colors.surface.primary};
        display: flex;
        align-items: center;
        justify-content: center;
    }
`
